import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Tooltip,
  MenuItem,
  Select,
} from "@material-ui/core";
import ShareIcon from "@material-ui/icons/Share";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { anchorOriginDefault, useStyles } from "./downloadApp.styles";
import { QRCodeSVG } from "qrcode.react";
import bikeCircledIcon from "assets/icons/bike-icon.svg"; // Adjust the path as necessary
import cicleWalverIcon from "assets/icons/circle-wavy-check-delve.svg"; // Adjust the path as necessary
import { TextConstants } from "constants/TextConstants";
import { COLORS } from "config/theme/baseTheme";
import { capitalizeFirstLetter } from "utils/textUtils";

interface Build {
  pageUrl: string;
  downloadUrl: string;
  version: string;
  build: string;
  releaseNotes: string;
  size: number;
  uploadedAt: string;
}

interface QRCardProps {
  title: string;
  versions: string[];
  data: Record<string, Build[]>;
  onReveal: (pageUrl: string) => void;
  revealedUrl: string;
  appDesc?: string;
  domain?: string;
}

const QRCodeContainer: React.FC<{
  build: Build;
  isRevealed: boolean;
  onReveal: (pageUrl: string) => void;
}> = React.memo(({ build, isRevealed, onReveal }) => {
  const classes = useStyles();

  return (
    <Box
      className={`${classes.qrCodeContainer} ${classes.box}`}
      onClick={!isRevealed ? () => onReveal(build.pageUrl) : undefined}
      style={{ cursor: !isRevealed ? "pointer" : "default" }}
    >
      <QRCodeSVG
        value={build.downloadUrl}
        size={192}
        level="H"
        className="qrCode"
        style={{ filter: isRevealed ? "none" : "blur(3px)" }}
      />
      {!isRevealed && <Box className={classes.overlayBox}>{TextConstants.CLICK_REVEAL}</Box>}
    </Box>
  );
});

const BuildActions: React.FC<{
  build: Build;
  isCopied: boolean;
  onCopy: (url: string) => void;
  onDownload: (url: string) => void;
}> = ({ build, isCopied, onCopy, onDownload }) => {
  const classes = useStyles();

  return (
    <>
      <Box
        className={`${classes.copyLink} ${classes.box}`}
        onClick={() => onCopy(build.downloadUrl)}
      >
        <Typography style={{ marginRight: 8, fontWeight: 600 }}>
          {isCopied ?  TextConstants.COPIED: TextConstants.COPY_LINK}
        </Typography>
        {isCopied ? (
          <CheckCircleIcon style={{ color: "green" }} />
        ) : (
          <ShareIcon className={classes.shareIcon} />
        )}
      </Box>
      <Button
        variant="contained"
        color="primary"
        style={{ textTransform: "none" }}
        className={classes.buttons}
        onClick={() => onDownload(build.pageUrl)}
      >
        {TextConstants.APP_DOWNLOAD_BUTTON_TEXT}
      </Button>
    </>
  );
};

const CardComponent: React.FC<QRCardProps> = ({
  title,
  versions,
  data,
  onReveal,
  revealedUrl,
  appDesc,
  domain,
}) => {
  const classes = useStyles();
  const [selectedVersion, setSelectedVersion] = useState(versions[0] || "");
  const [copied, setCopied] = useState(false);

  const builds = useMemo(() => data[selectedVersion] || [], [
    data,
    selectedVersion,
  ]);
  
  const isRevealed = useMemo(() => revealedUrl === builds[0]?.pageUrl, [
    revealedUrl,
    builds,
  ]);

    // Update selectedVersion when versions prop changes
    useEffect(() => {
      if (versions.length > 0) {
        setSelectedVersion(versions[0]);
      }
    }, [versions]);

  const handleRevealClick = useCallback(
    (pageUrl: string) => onReveal(pageUrl),
    [onReveal]
  );

  const handleDownloadClick = useCallback((downloadUrl: string) => {
    if (!downloadUrl) {
        return;
    }
    try {
      // Open the URL in a new tab
      window.open(downloadUrl, "_blank");
  } catch (error) {
      console.error("Error occurred while opening the URL:", error);
  }
}, []);

  const handleCopyLinkClick = useCallback((downloadUrl: string) => {
    navigator.clipboard
      .writeText(downloadUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error("Failed to copy link: ", err));
  }, []);

  const handleVersionChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setSelectedVersion(event.target.value as string);
    },
    []
  );

  return (
    <Box width={"98%"}>
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.box}>
            <Tooltip title={appDesc || ""} arrow>
            <Box display="flex" alignItems="center" className={classes.badgeElement}>
             
                  <img
                    src={title.toLowerCase().includes("delivery") ? bikeCircledIcon : cicleWalverIcon}
                    alt="Bike Icon"
                    style={{ marginRight: 8, width: 24, height: 24 }}
                  />
               
                <Typography className={`title`}>
                  {title}
                </Typography>
              </Box>
            </Tooltip>
          </Box>

          {domain && (
            <Box className={classes.box}>
              <Typography className="domain">{capitalizeFirstLetter(domain)}</Typography>
            </Box>
          )}

          {versions.length > 0 && ( // Conditionally render the Select dropdown
            <Box className={classes.selectBoxContainer}>
              <Select
                value={selectedVersion}
                onChange={handleVersionChange}
                variant="standard"
                disableUnderline
                className={classes.selectBox}
                displayEmpty
                renderValue={(value) => `Version ${value}`}
                MenuProps={{
                  disablePortal: true,
                  classes: {
                    list: classes.menuPropsList,
                    paper: classes.menuPropsPaper,
                  },
                  autoFocus: false,
                  getContentAnchorEl: null,
                  anchorOrigin: anchorOriginDefault,
                }}
              >
                {versions.map((version) => (
                  <MenuItem
                    key={version}
                    value={version}
                    style={{
                      padding: "2px 6px",
                      fontWeight: selectedVersion === version ? 700 : 400,
                      color: selectedVersion === version ? COLORS.SELECTED_BLUE : COLORS.BLACK,
                    }}
                  >
                    {version}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {builds.length > 0 ? (
            builds.map((build, index) => (
              <React.Fragment key={index}>
                <QRCodeContainer
                  build={build}
                  isRevealed={isRevealed}
                  onReveal={handleRevealClick}
                />
                <BuildActions
                  build={build}
                  isCopied={copied}
                  onCopy={handleCopyLinkClick}
                  onDownload={handleDownloadClick}
                />
              </React.Fragment>
            ))
          ) : (
            <Box className={classes.noDataBox}>
              <Typography className={classes.noDataText}> {TextConstants.NO_DATA_TXT}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CardComponent;
